import { config } from './config';
import { get_bracket } from './utils';

/**
 * Pure geometry for the marks drawn ON a node symbol — the adoption brackets, the
 * fertility crossbar and the proband arrow.
 *
 * These lived as inline d3 inside `Canvas.jsx`. They were lifted out for
 * TGCLOUD-1644, which puts the same notation in the right-click menu at 18px: an
 * icon that carries a *copy* of the geometry is an icon that silently stops
 * matching the chart the first time either side is tuned. `TileSymbol.jsx` already
 * works this way for the person symbols (via `nodeSymbolType`), and this is the
 * same idea for the marks.
 *
 * Every builder takes `size` — the symbol size the mark is drawn against — so one
 * formula serves both the chart (`config.SYMBOL_SIZE`, 155 chart units) and a menu
 * icon (18 CSS px). The default keeps `Canvas.jsx` call sites literal.
 *
 * Nothing here touches the DOM or d3; the caller appends and strokes.
 */

/**
 * Bennett adoption brackets — the square brackets that flank an adopted person's
 * symbol. Returns both brackets as one path string.
 */
export const adoptedBracketsPath = (size = config.SYMBOL_SIZE) => {
  const dx = -(size * 0.8);
  const dy = -(size * 0.8);
  const indent = size / 3;
  return (
    get_bracket(dx, dy, indent, size) + get_bracket(-dx, dy, -indent, size)
  );
};

/**
 * The fertility mark hung below a person symbol: a vertical stub down to a
 * crossbar, with a SECOND crossbar for infertility.
 *
 * `'fertile'` is the "No Issue" mark (one bar) and `'infertile'` is the
 * infertility mark (two). Anything else — including `null` — draws nothing, which
 * is what clears the mark when the value is set back to None.
 */
export const fertilityMarkPath = (fertility, size = config.SYMBOL_SIZE) => {
  const hw = size * 0.35;
  const vStart = size * 0.52;
  const hY = size * 0.85;
  const h2Y = size * 1.05;

  if (fertility === 'fertile') {
    return `M ${-hw} ${hY} H ${hw} M 0 ${vStart} V ${hY}`;
  }
  if (fertility === 'infertile') {
    return `M ${-hw} ${hY} H ${hw} M 0 ${vStart} V ${hY} M ${-hw} ${h2Y} H ${hw}`;
  }
  return '';
};

/**
 * The proband arrow's head, in SVG **marker** units — this is the `<path>` inside
 * the `<marker>`, not a coordinate in the node's own space. Shared so an icon that
 * draws the arrowhead inline gets the identical triangle.
 */
export const PROBAND_ARROW_HEAD_PATH = 'M 0 0 12 6 0 12 3 6';

/** `refX`/`refY`/`markerWidth`/`markerHeight` for the arrowhead marker. */
export const PROBAND_ARROW_MARKER = {
  refX: 12,
  refY: 6,
  markerWidth: 20,
  markerHeight: 20
};

/**
 * The proband arrow's shaft on the CHART, before rotation: drawn from far out on
 * the left in towards the symbol's edge, then rotated into the lower-left corner.
 *
 * The 200-unit tail is deliberately absolute rather than a multiple of `size` —
 * that is how `Canvas.jsx` has always drawn it, so the arrow keeps its length when
 * a deployment changes the symbol size through canvas preferences. Only the head
 * end tracks the symbol.
 *
 * A menu icon should NOT use this: a 200-unit tail makes no sense in an 18px box.
 * Icons share the head path and the rotation — the two things that would actually
 * be noticed if they drifted — and pick their own shaft length.
 */
export const PROBAND_ARROW_TAIL_X = -200;
export const PROBAND_ARROW_HEAD_INSET = 50;

export const probandArrowLine = (size = config.SYMBOL_SIZE) => ({
  x1: PROBAND_ARROW_TAIL_X,
  y1: 0,
  x2: -size + PROBAND_ARROW_HEAD_INSET,
  y2: 0
});

/**
 * Rotation for the proband arrow, in degrees.
 *
 * The default -45° is colinear with the deceased slash, so on a deceased node a
 * shallower angle is used: the arrow enters the lower-left from below the slash,
 * diverging into a clear "V".
 */
export const probandArrowRotation = (isDeceased) => (isDeceased ? -25 : -45);
